"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TrendingUp, Award, MapPin, Search, CircleCheck as CheckCircle2, ChartBar as BarChart3, ArrowUpRight, ShieldCheck, Target, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Link } from "@/components/common/Link";
const keywordBreakdown = [{
  tier: "Positions 1 - 3",
  count: 34,
  percentage: 24,
  description: "Top-of-page prime positions capturing maximum organic click share",
  colorClass: "bg-primary text-primary-foreground"
}, {
  tier: "Positions 4 - 10",
  count: 33,
  percentage: 23,
  description: "High-intent first page listings converting steady organic visitors",
  colorClass: "bg-accent text-accent-foreground"
}, {
  tier: "Positions 11 - 20",
  count: 76,
  percentage: 53,
  description: "Striking-distance page 2 keywords primed for direct page 1 push",
  colorClass: "bg-secondary text-secondary-foreground"
}];
const platformRankings = [{
  platform: "Apple Maps",
  rank: "#1",
  detail: "Top local listing across iOS search & navigation",
  icon: "MapPin"
}, {
  platform: "Bing Local",
  rank: "#1",
  detail: "Primary placement for regional enterprise queries",
  icon: "Search"
}, {
  platform: "Yelp Search",
  rank: "#1",
  detail: "Dominant category rank for commercial buyer searches",
  icon: "Award"
}, {
  platform: "Yellow Pages",
  rank: "#1",
  detail: "First-position presence across structured business directories",
  icon: "ShieldCheck"
}];
const localMetrics = [{
  metric: "77%",
  label: "Local Discovery Increase",
  description: "Direct increase in local search visibility across targeted postal areas.",
  progressVal: 77
}, {
  metric: "56%",
  label: "Website Click-Through Growth",
  description: "Higher qualified click rate from organic search to service landing pages.",
  progressVal: 56
}, {
  metric: "50%",
  label: "Phone & Form Inquiries",
  description: "Measured uplift in verified inbound phone calls and quotation requests.",
  progressVal: 50
}, {
  metric: "93%",
  label: "Target Area Market Share",
  description: "Local pack impression share secured across primary service radius.",
  progressVal: 93
}];
export default function VerifiedResults() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="verifiedresults" data-nav="light" className="bg-background text-foreground py-20 lg:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge variant="secondary" className="mb-4 px-3.5 py-1 text-xs font-semibold tracking-wide uppercase border border-border">
            <Sparkles className="size-3.5 text-accent mr-1.5" />
            Verified SEO Outcomes
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-default">
            Real Search Rankings &amp; Measured Growth
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            I believe in transparent reporting and hard search data. Here are the exact ranking and
            performance metrics achieved through my direct, hands-on SEO consultancy.
          </p>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.6
      }} className="mb-12">
          <Card className="border border-border bg-card shadow-lg rounded-xl overflow-hidden">
            <div className="p-6 sm:p-8 lg:p-10">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-5 flex flex-col justify-center">
                  <div className="inline-flex items-center gap-2 text-primary font-semibold text-sm mb-2">
                    <TrendingUp className="size-4" />
                    <span>Search Engine Visibility</span>
                  </div>
                  <div className="flex items-baseline gap-3">
                    <span className="text-5xl sm:text-6xl lg:text-7xl font-bold text-foreground font-default tracking-tight">
                      143
                    </span>
                    <span className="text-lg sm:text-xl font-medium text-muted-foreground">
                      Keywords on Page 1
                    </span>
                  </div>
                  <p className="mt-4 text-sm sm:text-base text-muted-foreground leading-relaxed">
                    A comprehensive spread of high-intent commercial keywords delivering continuous organic
                    traffic across Google UK and international search engines.
                  </p>
                  <div className="mt-6 flex items-center gap-3">
                    <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                      <Target className="size-5" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground font-default">
                        10 #1 Local Pack Rankings
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Locked into Google's top 3-pack for core local terms
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-7 flex flex-col gap-5 pt-6 lg:pt-0 border-t lg:border-t-0 lg:border-l border-border lg:pl-8">
                  <div className="text-sm font-semibold text-foreground uppercase tracking-wider">
                    Search Engine Position Distribution
                  </div>

                  {keywordBreakdown.map((tier, i) => <div key={tier.tier} data-index={i} className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-foreground">{tier.tier}</span>
                          <span className="text-xs px-2 py-0.5 rounded-md bg-muted text-muted-foreground font-medium">
                            {tier.count} Keywords
                          </span>
                        </div>
                        <span className="font-semibold text-primary">{tier.percentage}%</span>
                      </div>
                      <Progress value={tier.percentage} className="h-2.5 bg-muted" />
                      <p className="text-xs text-muted-foreground">{tier.description}</p>
                    </div>)}
                </div>
              </div>
            </div>
          </Card>
        </motion.div>

        <div className="mb-12">
          <div className="mb-6 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-foreground font-default">
                #1 Placement Across Major Local Directories
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Dominating map engines and structured search listings for high-trust authority.
              </p>
            </div>
            <Badge variant="outline" className="w-fit border-primary/30 text-primary font-medium">
              Verified Primary Placements
            </Badge>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.15
        }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {platformRankings.map((item, i) => <motion.div key={item.platform} variants={itemVariants} data-index={i}>
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-300 hover:border-primary/40 rounded-xl">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="p-2 rounded-lg bg-primary/10 text-primary">
                        {item.icon === "MapPin" && <MapPin className="size-5" />}
                        {item.icon === "Search" && <Search className="size-5" />}
                        {item.icon === "Award" && <Award className="size-5" />}
                        {item.icon === "ShieldCheck" && <ShieldCheck className="size-5" />}
                      </div>
                      <span className="text-2xl font-bold text-primary font-default">
                        {item.rank}
                      </span>
                    </div>
                    <CardTitle className="text-lg font-bold text-foreground font-default">
                      {item.platform}
                    </CardTitle>
                    <CardDescription className="text-xs text-muted-foreground mt-1">
                      {item.detail}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                      <CheckCircle2 className="size-3.5 text-chart-4" />
                      <span>Consistent Top Ranking</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}
          </motion.div>
        </div>

        <div className="mb-16">
          <div className="mb-6">
            <h3 className="text-xl sm:text-2xl font-bold text-foreground font-default">
              Local Search Performance &amp; Impact
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Verified metric gains after hands-on local SEO strategy implementation.
            </p>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.15
        }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {localMetrics.map((stat, i) => <motion.div key={stat.label} variants={itemVariants} data-index={i}>
                <Card className="h-full border border-border bg-card shadow-sm rounded-xl hover:shadow-md transition-[box-shadow,border-color] duration-300">
                  <CardHeader className="pb-2">
                    <div className="flex items-baseline justify-between mb-1">
                      <span className="text-4xl font-bold text-primary font-default">
                        {stat.metric}
                      </span>
                      <BarChart3 className="size-5 text-muted-foreground/60" />
                    </div>
                    <CardTitle className="text-base font-semibold text-foreground font-default">
                      {stat.label}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Progress value={stat.progressVal} className="h-1.5 mb-3 bg-muted" />
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {stat.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>)}
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="rounded-xl border border-border bg-muted/60 p-8 sm:p-10 text-center flex flex-col items-center justify-center shadow-xs">
          <h4 className="text-2xl sm:text-3xl font-bold text-foreground font-default">
            Ready for Verifiable Organic Search Results?
          </h4>
          <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl font-default">
            Every ranking, placement, and inquiry gain shown above is backed by authentic, verified performance data. Let's discuss how I can achieve similar measurable results for your business.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center gap-4">
            <Button asChild size="lg" className="rounded-md font-medium shadow-sm">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowUpRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="rounded-md font-medium">
              <Link to="/CaseStudiesPage">
                Explore Detailed Case Studies
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}